export default function StuartDestinationsSection() {
  const stats = [
    {
      value: "62%",
      label: "of Stuart requests are cruise, fishing, or boating-related with under 3 weeks notice",
    },
    {
      value: "2–4 weeks",
      label: "average notice for Bahamas fishing tournaments and Caribbean cruise departures",
    },
    {
      value: "Nov–Apr",
      label: "peak snowbird and sailfish season — passport renewals spike before departures",
    },
    {
      value: "48-hour",
      label: "shortest tournament departure window we've accommodated for a Stuart angler",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas",
      context: "The most common international destination for Stuart anglers and boaters — Bimini, the Abacos, Nassau, and the Exumas are all within easy reach of the Treasure Coast. Stuart's fishing fleet makes regular runs to the Bahamas for sailfish, marlin, and wahoo tournaments. Recreational boaters cruise to the Abacos for weeks at a time. All passengers on these vessels need valid passports for entry and re-entry.",
      urgency: "Fishing tournament invitations and charter openings often arrive with 1–2 weeks of notice — too short for standard processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Costa Rica",
      context: "Costa Rica is a premier sportfishing destination for Stuart anglers targeting Pacific sailfish, blue marlin, and dorado. The Los Sueños Resort and Marina, Quepos, and Golfito host international billfish tournaments that draw Stuart boats and anglers annually. Many Stuart fishing groups book Costa Rica trips during the winter tournament off-season.",
      urgency: "Tournament registration and charter booking windows are typically 2–4 weeks — insufficient time for standard passport processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Caribbean Cruises",
      context: "Stuart families, retirees, and snowbirds regularly cruise from Port Canaveral and Port Everglades to the Eastern and Western Caribbean — the Bahamas, Jamaica, the Cayman Islands, Cozumel, St. Thomas, and St. Maarten. Disney Cruise Line from Port Canaveral is especially popular with Stuart families. All passengers, including children, need valid passports for most itineraries.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Canada is a major destination for Stuart snowbirds and retirees during the summer months. Many Stuart seasonal residents maintain homes in Ontario, Quebec, or the Maritime provinces and need current passports for their seasonal return north. Canadian snowbirds living in Stuart seasonally also need valid passports for their return to Canada.",
      urgency: "Snowbirds often discover passport issues during seasonal transition planning — typically 2–4 weeks before their move dates",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico",
      context: "Cancun, Riviera Maya, Los Cabos, and Puerto Vallarta are staple beach vacation destinations for Stuart families and retirees. PBI offers connecting service through Atlanta, Charlotte, and Houston. Mexico requires six months of passport validity for entry, a rule that catches many Stuart travelers who assume their slightly expired passport will be accepted.",
      urgency: "Spring break and winter warm-weather bookings surge in January — passport discoveries spike in February and March for Stuart families",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom & Ireland",
      context: "London, Edinburgh, and Dublin draw Stuart medical professionals attending Cleveland Clinic conferences, retirees on European heritage tours, and snowbirds extending their travels. Direct flights from PBI connect through Charlotte, Atlanta, and Philadelphia. The UK requires passports valid for the duration of your stay.",
      urgency: "Medical conference invitations and European cruise bookings typically arrive 3–6 weeks before departure — tight for standard processing",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Stuart Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Stuart
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Stuart travelers head to the Bahamas and Costa Rica for sportfishing, the Caribbean for cruises, Canada for seasonal transitions, and Europe for medical conferences and heritage travel. These are the destinations that create the most passport urgency for our Stuart clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout the Treasure Coast</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Jupiter", href: "/cities/jupiter" },
              { name: "Palm Beach Gardens", href: "/cities/palm-beach-gardens" },
              { name: "West Palm Beach", href: "/cities/west-palm-beach" },
              { name: "Vero Beach", href: "/vero-beach" },
              { name: "Port St. Lucie", href: "/cities/port-st-lucie" },
              { name: "Naples", href: "/cities/naples" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}